<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef, watch } from "vue";
import { useRoute } from "vue-router";
import CategoryRow from "../components/CategoryRow.vue";
import EmailConfirmation from "../components/EmailConfirmation.vue";
import EventFlyer from "../components/EventFlyer.vue";
import TestBanner from "../components/TestBanner.vue";
import EventUnavailable from "./EventUnavailable.vue";
import { agbPdfFor, flyerFor } from "../eventAssets";
import {
  EVENT_INFO_URL,
  MEMBERSHIP_INFO_URL,
  PRIVACY_POLICY_URL,
} from "../siteLinks";
import { useEvent } from "../composables/useEvent";
import { useCart } from "../composables/useCart";
import { ApiError, createPurchase } from "../api";
import { loadBuyDraft, saveBuyDraft } from "../buyDraft";
import type { CreatePurchaseResponse } from "@evka/shared";

const route = useRoute();

const eventId = computed<string>(() => {
  // Canonical: /event/<slug> path param; legacy: /?eventId=<slug>.
  const p = route.params.eventId;
  if (typeof p === "string" && p) return p;
  const v = route.query.eventId;
  return typeof v === "string" ? v : "";
});

const isTest = computed<boolean>(() => {
  const v = route.query.mode;
  return typeof v === "string" && v === "test";
});

const { event, loading, error, errorCode, load } = useEvent();
const cart = useCart(event);

const flyer = computed(() => flyerFor(eventId.value));
const agbPdf = computed(() => agbPdfFor(eventId.value));

const email = ref("");
const confirmEmail = ref("");
const submitting = ref(false);
const submitError = ref<string | null>(null);
// Deliberately not part of the sessionStorage draft: after a failed SumUp
// round-trip the customer must actively re-accept the AGB.
const termsAccepted = ref(false);

const emailRef = useTemplateRef<{ isReady: boolean; normalizedEmail: string }>(
  "emailRef",
);

/**
 * Refusal gate (SPEC §8.1). Priority mirrors the backend: over (event day
 * passed — automatic) > ended > paused (admin stop) > inactive (unpublished).
 * Test mode never refuses — it shows the yellow banner plus warning badges
 * instead, so flows stay rehearsable.
 */
const refusalVariant = computed<"inactive" | "paused" | "ended" | "over" | null>(() => {
  if (event.value === null || isTest.value) return null;
  const status = event.value.salesStatus;
  if (status === "over" || status === "ended" || status === "paused") return status;
  if (!event.value.active) return "inactive";
  return null;
});
const showInactiveRefusal = computed(() => refusalVariant.value !== null);
const showInactiveBadge = computed(
  () => event.value !== null && !event.value.active && isTest.value,
);
const showStoppedBadge = computed(
  () => event.value !== null && event.value.salesStatus !== "open" && isTest.value,
);
const formDisabled = computed(() => showInactiveRefusal.value || submitting.value);
const submitDisabled = computed(() => {
  if (formDisabled.value) return true;
  if (!cart.canSubmit.value) return true;
  if (!emailRef.value?.isReady) return true;
  if (!termsAccepted.value) return true;
  return false;
});

const disabledReason = computed<string | null>(() => {
  if (showInactiveRefusal.value) return null; // page already explains it
  const cartReason = cart.submitBlockedReason.value;
  if (cartReason) return cartReason;
  if (!termsAccepted.value) return "buy.submit.disabledTerms";
  return null;
});

onMounted(async () => {
  if (!eventId.value) return;
  await load(eventId.value);
  if (event.value) restoreDraft();
});

/**
 * Rehydrate the form from sessionStorage after a SumUp round-trip that ended
 * in failed/abandoned/timed-out — the customer clicked "Zurück zum Ticketkauf"
 * on /return and we want them to keep their email and ticket selection. The
 * /return page clears the draft on `completed`, so a fresh visit after a
 * successful purchase starts blank.
 *
 * Order matters: set isMember first so the cart's max-quantity caps reflect
 * the right mode before we apply the saved quantities (setAdultQty clamps).
 */
function restoreDraft() {
  const draft = loadBuyDraft(eventId.value);
  if (!draft) return;
  cart.isMember.value = draft.isMember;
  cart.setAdultQty(draft.adultQty);
  cart.setChildQty(draft.childQty);
  email.value = draft.email;
  confirmEmail.value = draft.email;
}

watch(eventId, async (next, prev) => {
  if (next && next !== prev) {
    await load(next);
  }
});

async function onSubmit() {
  if (submitDisabled.value || !event.value || !emailRef.value) return;
  submitting.value = true;
  submitError.value = null;

  try {
    const res: CreatePurchaseResponse = await createPurchase({
      eventId: event.value.eventId,
      // Always send the normalized form. The confirmation field never reaches
      // the server (SPEC §8.1 step 6).
      customerEmail: emailRef.value.normalizedEmail,
      items: cart.items.value,
      isTest: isTest.value,
    });

    const url = pickCheckoutUrl(res);
    if (!url) {
      submitError.value = "checkoutMissingUrl";
      submitting.value = false;
      return;
    }
    // Snapshot the form so the customer can pick up where they left off if
    // the SumUp round-trip ends in failed/abandoned/timed-out. Cleared by
    // /return on `completed`. See buyDraft.ts.
    saveBuyDraft({
      eventId: event.value.eventId,
      email: emailRef.value.normalizedEmail,
      isMember: cart.isMember.value,
      adultQty: cart.adultQty.value,
      childQty: cart.childQty.value,
    });
    window.location.href = url;
  } catch (err) {
    submitting.value = false;
    if (err instanceof ApiError) {
      submitError.value = err.message;
    } else {
      submitError.value = err instanceof Error ? err.message : String(err);
    }
  }
}

function pickCheckoutUrl(res: CreatePurchaseResponse): string | null {
  const direct = res.sumupCheckout.url;
  if (typeof direct === "string" && direct.length > 0) return direct;
  // SumUp's hosted checkout sometimes shows up under hosted_checkout_url —
  // fall back to that (and any reasonable URL-shaped extra) before giving up.
  const hosted = res.sumupCheckout["hosted_checkout_url"];
  if (typeof hosted === "string" && hosted.length > 0) return hosted;
  const fallback = res.sumupCheckout["checkout_url"];
  if (typeof fallback === "string" && fallback.length > 0) return fallback;
  return null;
}
</script>

<template>
  <div class="mx-auto max-w-xl px-4 py-6" :class="flyer ? 'md:max-w-5xl' : ''">
    <h1 class="sr-only">{{ $t("buy.page.title") }}</h1>

    <p v-if="loading" class="text-slate-600">{{ $t("buy.loading.event") }}</p>

    <div
      v-else-if="!eventId"
      class="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-red-800"
    >
      {{ $t("buy.errors.missingEventId") }}
    </div>

    <div
      v-else-if="error && !event"
      class="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-red-800"
    >
      {{ $t("buy.errors.eventLoadFailed") }}
      <span class="ml-2 text-xs text-red-600" v-if="errorCode">[{{ errorCode }}]</span>
    </div>

    <template v-else-if="event">
      <TestBanner
        v-if="isTest"
        :inactive="showInactiveBadge"
        :stopped="showStoppedBadge"
        class="mb-4"
      />

      <EventUnavailable v-if="refusalVariant" :variant="refusalVariant" />

      <div v-else>
        <form class="flex flex-col gap-5" @submit.prevent="onSubmit">
          <!-- Two-column zone (md+): flyer left; event heading, membership and
               tickets right. Everything after it spans the full width again. -->
          <div
            :class="flyer ? 'md:grid md:grid-cols-[minmax(0,24rem)_minmax(0,1fr)] md:items-start md:gap-8' : ''"
          >
            <div v-if="flyer" class="mb-5 md:mb-0">
              <EventFlyer :flyer="flyer" :event-name="event.name" />
            </div>

            <div class="flex flex-col gap-5">
        <header>
          <h2 class="text-2xl font-semibold text-slate-900">{{ event.name }}</h2>
          <dl class="mt-2 text-sm text-slate-700">
            <div class="flex flex-wrap gap-x-2">
              <dt class="font-medium">{{ $t("buy.event.dateLabel") }}:</dt>
              <dd>{{ $d(new Date(`${event.eventDate}T00:00:00Z`), "long", "de-CH") }}</dd>
            </div>
            <div class="mt-1 flex flex-wrap gap-x-2">
              <dt class="font-medium">{{ $t("buy.event.venueLabel") }}:</dt>
              <dd>{{ event.venue }}</dd>
            </div>
          </dl>
        </header>

          <section
            class="rounded-md border border-slate-200 bg-white p-4"
            aria-labelledby="buy-membership-label"
          >
            <label class="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                class="mt-1 h-4 w-4 rounded border-slate-400 text-slate-900 focus:ring-slate-500"
                :checked="cart.isMember.value"
                :disabled="formDisabled"
                @change="cart.isMember.value = ($event.target as HTMLInputElement).checked"
              />
              <span class="flex flex-col">
                <span id="buy-membership-label" class="text-sm font-medium text-slate-900">
                  {{ $t("buy.membership.label") }}
                </span>
                <span class="text-xs text-slate-600">{{ $t("buy.membership.hint") }}</span>
              </span>
            </label>
            <details class="mt-3" data-testid="membership-details">
              <summary
                class="cursor-pointer text-xs font-medium text-slate-700 underline decoration-dotted"
              >
                {{ $t("buy.membership.detailsSummary") }}
              </summary>
              <div class="mt-2 space-y-2 text-xs text-slate-600">
                <p>{{ $t("buy.membership.detailsWho") }}</p>
                <p>{{ $t("buy.membership.detailsAttribution") }}</p>
                <p>
                  <i18n-t keypath="buy.membership.detailsJoin" tag="span" scope="global">
                    <template #link>
                      <a
                        :href="MEMBERSHIP_INFO_URL"
                        target="_blank"
                        rel="noopener"
                        class="underline hover:text-slate-900"
                      >{{ $t("buy.membership.joinLinkLabel") }}</a>
                    </template>
                  </i18n-t>
                </p>
              </div>
            </details>
          </section>

          <section
            class="rounded-md border border-slate-200 bg-white p-4"
            aria-labelledby="buy-categories-title"
          >
            <h3
              id="buy-categories-title"
              class="text-sm font-semibold uppercase tracking-wide text-slate-700"
            >
              {{ $t("buy.categories.title") }}
            </h3>
            <p class="mt-1 text-sm text-slate-600" data-testid="tickets-intro">
              {{ $t("buy.categories.intro") }}
            </p>
            <CategoryRow
              :label="cart.isMember.value
                ? $t('buy.categories.accompanyingAdultLabel')
                : `${$t('buy.categories.adult')} ${$t('buy.categories.nonMemberSuffix')}`"
              :unit-price="cart.adultUnitPrice.value"
              :quantity="cart.adultQty.value"
              :max="cart.adultMax.value"
              :disabled="formDisabled"
              @update:quantity="cart.setAdultQty"
            >
              <template v-if="cart.isMember.value" #price>
                {{ $t("buy.categories.free") }}<sup>*</sup>
              </template>
            </CategoryRow>
            <p
              v-if="cart.isMember.value"
              class="mt-1 text-xs text-slate-500"
              data-testid="accompanying-adult-hint"
            >
              {{ $t("buy.categories.accompanyingAdultHint") }}
            </p>
            <CategoryRow
              :label="`${$t('buy.categories.child')} ${cart.isMember.value ? $t('buy.categories.memberSuffix') : $t('buy.categories.nonMemberSuffix')}`"
              :unit-price="cart.childUnitPrice.value"
              :quantity="cart.childQty.value"
              :max="cart.childMax.value"
              :disabled="formDisabled"
              @update:quantity="cart.setChildQty"
            />
            <p class="mt-1 text-xs text-slate-500" data-testid="child-age-hint">
              {{ $t("buy.categories.childAgeHint") }}
            </p>

            <div class="mt-4 flex items-center justify-between border-t border-slate-200 pt-3">
              <span class="text-base font-semibold text-slate-900">
                {{ $t("buy.summary.total") }}
              </span>
              <span
                class="text-lg font-bold tabular-nums text-slate-900"
                data-testid="cart-total"
              >
                {{ $n(Number(cart.totalAmount.value), "currency", "de-CH") }}
              </span>
            </div>
          </section>

          <div class="text-xs text-slate-500" data-testid="payment-options">
            <p>{{ $t("buy.paymentOptions.online") }}</p>
            <p class="mt-0.5">{{ $t("buy.paymentOptions.twint") }}</p>
          </div>
            </div>
          </div>

          <section
            class="rounded-md border border-slate-200 bg-white p-4"
            aria-labelledby="buy-good-to-know-title"
          >
            <h3
              id="buy-good-to-know-title"
              class="text-sm font-semibold uppercase tracking-wide text-slate-700"
            >
              {{ $t("buy.goodToKnow.title") }}
            </h3>
            <ul class="mt-2 list-disc space-y-1.5 pl-5 text-sm text-slate-700">
              <li>{{ $t("buy.goodToKnow.publicArea") }}</li>
              <li>{{ $t("buy.goodToKnow.childcare") }}</li>
              <li>{{ $t("buy.goodToKnow.weather") }}</li>
              <li>{{ $t("buy.goodToKnow.delivery") }}</li>
              <li>
                <i18n-t keypath="buy.goodToKnow.moreInfo" tag="span" scope="global">
                  <template #link>
                    <a
                      :href="EVENT_INFO_URL"
                      target="_blank"
                      rel="noopener"
                      class="underline hover:text-slate-900"
                    >{{ $t("buy.goodToKnow.moreInfoLinkLabel") }}</a>
                  </template>
                </i18n-t>
              </li>
            </ul>
          </section>

          <!-- Checkout tail: narrower than the page, left-aligned under the flyer. -->
          <div class="flex max-w-xl flex-col gap-5">
          <EmailConfirmation
            ref="emailRef"
            :email="email"
            :confirm-email="confirmEmail"
            :disabled="formDisabled"
            @update:email="email = $event"
            @update:confirm-email="confirmEmail = $event"
          />

          <!-- Only the checkbox itself accepts — the text is NOT a label, so a
               stray click on the sentence (or its links) never toggles consent. -->
          <section class="rounded-md border border-slate-200 bg-white p-4">
            <div class="flex items-start gap-3">
              <input
                id="buy-terms-checkbox"
                type="checkbox"
                class="mt-1 h-4 w-4 cursor-pointer rounded border-slate-400 text-slate-900 focus:ring-slate-500"
                data-testid="terms-checkbox"
                aria-labelledby="buy-terms-label"
                :checked="termsAccepted"
                :disabled="formDisabled"
                @change="termsAccepted = ($event.target as HTMLInputElement).checked"
              />
              <span id="buy-terms-label" class="text-sm text-slate-800">
                <i18n-t keypath="buy.terms.acceptLabel" tag="span" scope="global">
                  <template #agb>
                    <!-- Downloads the versioned per-event AGB PDF; falls back
                         to plain text for an event without one mapped. -->
                    <a
                      v-if="agbPdf"
                      :href="agbPdf"
                      download
                      class="underline hover:text-slate-900"
                      data-testid="terms-open"
                    >{{ $t("buy.terms.agbLinkLabel") }}</a>
                    <span v-else>{{ $t("buy.terms.agbLinkLabel") }}</span>
                  </template>
                  <template #privacy>
                    <a
                      :href="PRIVACY_POLICY_URL"
                      target="_blank"
                      rel="noopener"
                      class="underline hover:text-slate-900"
                    >{{ $t("buy.terms.privacyLinkLabel") }}</a>
                  </template>
                </i18n-t>
              </span>
            </div>
          </section>

          <div v-if="submitError" class="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
            {{ submitError === "checkoutMissingUrl"
              ? $t("buy.errors.checkoutMissingUrl")
              : $t("buy.errors.purchaseFailed") }}
          </div>

          <div class="flex flex-col gap-2">
            <p
              v-if="disabledReason"
              class="text-center text-sm font-medium text-red-700"
              data-testid="submit-disabled-reason"
            >
              {{ $t(disabledReason) }}
            </p>
            <button
              type="submit"
              class="inline-flex items-center justify-center rounded-md bg-slate-900 px-4 py-3 text-base font-semibold text-white shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-400"
              :disabled="submitDisabled"
            >
              <span v-if="submitting">{{ $t("buy.submit.loading") }}</span>
              <span v-else>{{ $t("buy.submit.label") }}</span>
            </button>
          </div>
          </div>
        </form>
      </div>
    </template>
  </div>
</template>
